(() => {
  const SELECTOR = '[data-animated-texture]';
  const syncHomeBandTextureConfig = () => {
    const source = document.querySelector('[data-home-texture-source]');
    const target = document.querySelector('.home-yellow-band [data-animated-texture]');
    if (!source || !target) return;

    const enabled = source.dataset.homeTextureEnabled !== 'false';
    target.style.display = enabled ? '' : 'none';

    target.dataset.textureType = source.dataset.textureType || target.dataset.textureType || 'diffuse';
    target.dataset.density = source.dataset.density || target.dataset.density || '55';
    target.dataset.opacity = source.dataset.opacity || target.dataset.opacity || '75';
    target.dataset.speed = source.dataset.speed || target.dataset.speed || '100';
    target.dataset.settle = source.dataset.settle || target.dataset.settle || '65';

    if (source.dataset.texturePrimaryColor) {
      target.style.setProperty('--st-texture-primary', source.dataset.texturePrimaryColor);
    }
    if (source.dataset.textureSecondaryColor) {
      target.style.setProperty('--st-texture-secondary', source.dataset.textureSecondaryColor);
    }
    if (source.dataset.textureParticleColor) {
      target.style.setProperty('--st-texture-particle', source.dataset.textureParticleColor);
    }
    if (source.dataset.textureAccentColor) {
      target.style.setProperty('--st-texture-accent', source.dataset.textureAccentColor);
    }
    if (target.dataset.opacity) {
      target.style.setProperty('--st-texture-opacity', `${Number(target.dataset.opacity) / 100}`);
    }
  };

  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  const supportsCanvas = !!document.createElement('canvas').getContext;
  syncHomeBandTextureConfig();
  const roots = Array.from(document.querySelectorAll(SELECTOR));

  if (!roots.length) return;

  if (!supportsCanvas || reducedMotion) {
    roots.forEach((root) => root.setAttribute('data-canvas-ready', 'false'));
    return;
  }

  const lowPowerDevice =
    (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) ||
    (navigator.deviceMemory && navigator.deviceMemory <= 4) ||
    window.matchMedia?.('(pointer: coarse)')?.matches;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const random = (min, max) => min + Math.random() * (max - min);
  const parseNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const resolveVisibility = (root) => {
    const rect = root.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const start = vh;
    const end = -rect.height;
    return clamp((start - rect.top) / (start - end), 0, 1);
  };

  class AnimatedTextureInstance {
    constructor(root) {
      this.root = root;
      this.canvas = root.querySelector('.st-animated-texture__canvas');
      this.ctx = this.canvas?.getContext('2d', { alpha: true });
      this.particles = [];
      this.active = false;
      this.lastTime = performance.now();
      this.time = 0;
      this.scrollVelocity = 0;
      this.scrollFactor = 0;
      this.visibleAmount = 0;
      this.width = 0;
      this.height = 0;
      this.dpr = 1;
      this.syncSettings();
      this.handleResize = () => this.resize();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.root);
      } else {
        window.addEventListener('resize', this.handleResize, { passive: true });
      }
      this.resize();
      this.root.setAttribute('data-canvas-ready', 'true');
    }

    destroy() {
      this.resizeObserver?.disconnect();
      if (!this.resizeObserver) {
        window.removeEventListener('resize', this.handleResize);
      }
    }

    syncSettings() {
      this.type = this.root.dataset.textureType || 'diffuse';
      this.baseDensity = parseNumber(this.root.dataset.density, 56);
      this.baseOpacity = clamp(parseNumber(this.root.dataset.opacity, 72) / 100, 0.1, 1);
      this.baseSpeed = clamp(parseNumber(this.root.dataset.speed, 100) / 100, 0.4, 1.8);
      this.settleStrength = clamp(parseNumber(this.root.dataset.settle, 62) / 100, 0.2, 1.3);
      if (this.particles.length) this.buildParticles();
    }

    setActive(active) {
      this.active = active;
      if (active) this.lastTime = performance.now();
    }

    resize() {
      const rect = this.root.getBoundingClientRect();
      this.width = Math.max(1, Math.round(rect.width));
      this.height = Math.max(1, Math.round(rect.height));
      this.dpr = clamp(window.devicePixelRatio || 1, 1, lowPowerDevice ? 1.2 : 1.6);
      this.canvas.width = Math.round(this.width * this.dpr);
      this.canvas.height = Math.round(this.height * this.dpr);
      this.canvas.style.width = `${this.width}px`;
      this.canvas.style.height = `${this.height}px`;
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      this.buildParticles();
    }

    buildParticles() {
      const areaFactor = (this.width * this.height) / 18000;
      const mobileFactor = lowPowerDevice ? 0.56 : 1;
      const count = clamp(Math.round((this.baseDensity * areaFactor) / 14 * mobileFactor), 26, lowPowerDevice ? 90 : 180);
      this.particles = Array.from({ length: count }, () => this.createParticle(true));
    }

    createParticle(initial = false) {
      const edgePadding = this.width * 0.08;
      return {
        x: random(-edgePadding, this.width + edgePadding),
        y: initial ? random(-this.height * 0.2, this.height * 0.7) : random(-this.height * 0.28, -12),
        radius: random(lowPowerDevice ? 0.7 : 0.8, lowPowerDevice ? 1.9 : 2.6),
        alpha: random(0.16, 0.9),
        vx: random(-0.05, 0.05),
        vy: random(0.04, 0.12),
        seed: random(0, Math.PI * 2),
        swirl: random(0.2, 1),
        drift: random(0.4, 1.6),
      };
    }

    recycleParticle(particle) {
      const replacement = this.createParticle(false);
      Object.assign(particle, replacement);
    }

    setScrollVelocity(scrollVelocity) {
      this.scrollVelocity = scrollVelocity;
    }

    step(now) {
      const dt = clamp(now - this.lastTime, 12, 32);
      this.lastTime = now;
      this.time += dt;
      this.visibleAmount = resolveVisibility(this.root);

      const viewCurve = Math.sin(this.visibleAmount * Math.PI);
      const velocityInfluence = clamp(Math.abs(this.scrollVelocity) / 42, 0, 1.2);
      this.scrollFactor = 1 + velocityInfluence * 0.7 + viewCurve * 0.4;

      this.root.style.setProperty('--st-texture-pulse', (1 + Math.sin(this.time * 0.00055) * 0.04).toFixed(4));
      this.root.style.setProperty('--st-texture-drift-x', `${Math.sin(this.time * 0.00028) * 10}px`);
      this.root.style.setProperty('--st-texture-drift-y', `${Math.cos(this.time * 0.00022) * 8}px`);
      this.root.style.setProperty('--st-texture-wave-shift', `${Math.sin(this.time * 0.00042) * 18}px`);

      this.draw(dt, viewCurve, velocityInfluence);
    }

    draw(dt, viewCurve, velocityInfluence) {
      if (!this.ctx) return;
      this.ctx.clearRect(0, 0, this.width, this.height);

      const gradient = this.ctx.createLinearGradient(0, 0, 0, this.height);
      gradient.addColorStop(0, 'rgba(255,255,255,0)');
      gradient.addColorStop(0.22, this.withAlpha('--st-texture-secondary', this.baseOpacity * 0.16));
      gradient.addColorStop(0.55, this.withAlpha('--st-texture-primary', this.baseOpacity * 0.38));
      gradient.addColorStop(0.82, this.withAlpha('--st-texture-accent', this.baseOpacity * 0.18));
      gradient.addColorStop(1, 'rgba(255,255,255,0)');
      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(0, 0, this.width, this.height);

      const fieldBase = this.type === 'swirl' ? 0.018 : this.type === 'ripple' ? 0.012 : 0.009;
      const settleBase = this.settleStrength * this.baseSpeed * (0.18 + viewCurve * 0.2 + velocityInfluence * 0.14);
      const rippleStrength = this.type === 'ripple' ? 0.95 : this.type === 'swirl' ? 0.55 : 0.35;
      const swirlStrength = this.type === 'swirl' ? 1.3 : 0.8;
      const dtScale = dt / 16.666;

      for (const particle of this.particles) {
        const ripple = Math.sin((particle.x * 0.018) + (this.time * 0.0016) + particle.seed) * rippleStrength;
        const curl = Math.cos((particle.y * 0.02) + (this.time * 0.0011) + particle.seed) * particle.swirl * swirlStrength;
        const settle = settleBase + Math.sin((particle.y / Math.max(this.height, 1)) * Math.PI * 2 + particle.seed) * 0.05;
        const scrollPush = this.scrollVelocity * 0.00016 * particle.drift;

        particle.vx += (curl * fieldBase + ripple * 0.03 + scrollPush) * dtScale;
        particle.vy += settle * dtScale;
        particle.vx *= 0.985;
        particle.vy *= 0.94;

        particle.x += particle.vx * this.scrollFactor * this.baseSpeed;
        particle.y += particle.vy * this.scrollFactor * this.baseSpeed;

        if (particle.x < -24) particle.x = this.width + 24;
        if (particle.x > this.width + 24) particle.x = -24;
        if (particle.y > this.height + 28) this.recycleParticle(particle);

        const alpha = particle.alpha * (0.18 + viewCurve * 0.72);
        this.ctx.fillStyle = this.withAlpha('--st-texture-particle', alpha);
        this.ctx.beginPath();
        this.ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        this.ctx.fill();
      }
    }

    withAlpha(cssVariable, alpha) {
      const value = getComputedStyle(this.root).getPropertyValue(cssVariable).trim();
      return this.hexToRgba(value || '#ffffff', alpha);
    }

    hexToRgba(hex, alpha) {
      const normalized = hex.replace('#', '').trim();
      const expanded = normalized.length === 3 ? normalized.split('').map((char) => char + char).join('') : normalized;
      const numeric = Number.parseInt(expanded, 16);
      const r = (numeric >> 16) & 255;
      const g = (numeric >> 8) & 255;
      const b = numeric & 255;
      return `rgba(${r}, ${g}, ${b}, ${clamp(alpha, 0, 1)})`;
    }
  }

  const instances = roots.map((root) => new AnimatedTextureInstance(root));
  let rafId = null;
  let lastScrollY = window.scrollY;
  let globalScrollVelocity = 0;

  const updateScrollVelocity = () => {
    const currentScrollY = window.scrollY;
    const delta = currentScrollY - lastScrollY;
    lastScrollY = currentScrollY;
    globalScrollVelocity = globalScrollVelocity * 0.82 + delta * 0.18;
  };

  const schedule = () => {
    if (rafId != null) return;
    rafId = window.requestAnimationFrame(tick);
  };

  const tick = (now) => {
    rafId = null;
    let hasActiveInstance = false;

    updateScrollVelocity();

    for (const instance of instances) {
      if (!instance.active) continue;
      hasActiveInstance = true;
      instance.setScrollVelocity(globalScrollVelocity);
      instance.step(now);
    }

    if (hasActiveInstance) schedule();
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const instance = instances.find((item) => item.root === entry.target);
        if (!instance) continue;
        instance.setActive(entry.isIntersecting);
      }
      schedule();
    },
    {
      threshold: 0.01,
    }
  );

  instances.forEach((instance) => observer.observe(instance.root));

  const refreshInstances = (scope) => {
    syncHomeBandTextureConfig();
    instances.forEach((instance) => instance.syncSettings());
    const newRoots = Array.from((scope || document).querySelectorAll(SELECTOR));
    newRoots.forEach((root) => {
      if (instances.some((instance) => instance.root === root)) return;
      const instance = new AnimatedTextureInstance(root);
      instances.push(instance);
      observer.observe(root);
    });
    schedule();
  };

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('shopify:section:load', (event) => refreshInstances(event.target));
  document.addEventListener('shopify:section:reorder', schedule);
  document.addEventListener('shopify:section:select', () => {
    syncHomeBandTextureConfig();
    instances.forEach((instance) => instance.syncSettings());
    schedule();
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) schedule();
  });

  schedule();
})();
